export const PATH_STYLES = {
  road: { label: 'Road', strength: 0.28, frequency: 0.65 },
  trail: { label: 'Trail', strength: 0.5, frequency: 1.1 },
  lowland: { label: 'Lowland river', strength: 1, frequency: 1 },
  mountain: { label: 'Mountain river', strength: 0.6, frequency: 1.8 },
  canal: { label: 'Canal', strength: 0, frequency: 1 },
};

export const pathLength = points => points.slice(1).reduce((sum, p, i) =>
  sum + Math.hypot(p.x - points[i].x, p.z - points[i].z), 0);

export const pathSections = place => {
  const data = place?.shape?.shapeData || {};
  return Array.isArray(data.paths) ? data.paths : [data.points || data.vertices || []];
};

const crosses = (a, b, c, d) => {
  const side = (p, q, r) => (q.x - p.x) * (r.z - p.z) - (q.z - p.z) * (r.x - p.x);
  return side(a, b, c) * side(a, b, d) < 0 && side(c, d, a) * side(c, d, b) < 0;
};
export const selfCrosses = points => {
  for (let i = 1; i < points.length; i += 1) {
    for (let j = i + 2; j < points.length; j += 1) {
      if (crosses(points[i - 1], points[i], points[j - 1], points[j])) return true;
    }
  }
  return false;
};

// Coordinates and distances are feet. Every supplied anchor remains exact.
export function naturalizePath(anchors, { style = 'road', winding = 60, deviation = 5280,
  bendScale = 26400, seed = 1 } = {}) {
  if (!Array.isArray(anchors) || anchors.length < 2 || anchors.some(p =>
    !Number.isFinite(p.x) || !Number.isFinite(p.z))) throw new Error('The path needs at least two valid points.');
  if (![winding, deviation, bendScale, seed].every(Number.isFinite) || deviation < 0 || bendScale <= 0)
    throw new Error('Enter valid distances and a numeric seed.');
  const profile = PATH_STYLES[style];
  if (!profile) throw new Error('Choose a path style.');
  if (anchors.length > 3000) throw new Error('Select a shorter section (at most 3,000 anchors).');
  let state = seed >>> 0;
  const random = () => { state = (Math.imul(state, 1664525) + 1013904223) >>> 0; return state / 4294967296; };
  const phases = anchors.slice(1).map(() => [random() * Math.PI * 2, random() * Math.PI * 2, random()]);
  const totalLength = pathLength(anchors);
  const spacing = Math.max(bendScale / 24, totalLength / Math.max(1, 3000 - anchors.length));
  for (let attempt = 0; attempt < 9; attempt += 1) {
    const result = [anchors[0]];
    anchors.slice(1).forEach((end, index) => {
      const start = anchors[index];
      const dx = end.x - start.x, dz = end.z - start.z;
      const length = Math.hypot(dx, dz);
      const steps = Math.max(1, Math.ceil(length / spacing));
      const [phase, smallPhase, variation] = phases[index];
      const amplitude = Math.min(deviation, length * 0.2) * profile.strength *
        Math.max(0, Math.min(100, winding)) / 100 * (0.7 + variation * 0.3) * (0.5 ** attempt);
      for (let step = 1; step < steps; step += 1) {
        const t = step / steps;
        const angle = t * length / bendScale * Math.PI * 2 * profile.frequency;
        const offset = amplitude * Math.sin(Math.PI * t) ** 2 *
          (0.75 * Math.sin(angle + phase) + 0.25 * Math.sin(angle * 2.37 + smallPhase));
        result.push({ ...start, x: start.x + dx * t - dz / length * offset,
          z: start.z + dz * t + dx / length * offset,
          ...(Number.isFinite(start.y) && Number.isFinite(end.y) ? { y: start.y + (end.y - start.y) * t } : {}) });
      }
      result.push(end);
    });
    if (!selfCrosses(result)) return result;
  }
  throw new Error('This section crosses itself. Move its anchors or select a shorter section.');
}

export function applyNaturalPath(place, points, sectionIndex, settings, anchors) {
  const data = place.shape.shapeData || {};
  const sections = pathSections(place).map((section, i) => i === sectionIndex ? points : section);
  const all = sections.flat();
  const halfWidth = Math.max(0, Number(data.width) || 0) / 2;
  const bounds = all.reduce((b, p) => ({ minX: Math.min(b.minX, p.x - halfWidth),
    maxX: Math.max(b.maxX, p.x + halfWidth), minZ: Math.min(b.minZ, p.z - halfWidth),
    maxZ: Math.max(b.maxZ, p.z + halfWidth) }),
  { minX: Infinity, maxX: -Infinity, minZ: Infinity, maxZ: -Infinity });
  const key = Array.isArray(data.paths) ? 'paths' : Array.isArray(data.points) ? 'points' : 'vertices';
  const nextData = { ...data, [key]: key === 'paths' ? sections : points,
    naturalPath: { version: 1, sectionIndex, settings, anchors, generatedPoints: points } };
  // Keep legacy aliases synchronized when both are present.
  if (key === 'points' && Array.isArray(data.vertices)) nextData.vertices = points;
  return { ...place, location: { ...place.location, x: (bounds.minX + bounds.maxX) / 2,
    z: (bounds.minZ + bounds.maxZ) / 2,
    maxRadius: Math.hypot(bounds.maxX - bounds.minX, bounds.maxZ - bounds.minZ) / 2 },
  shape: { ...place.shape, ...bounds, shapeData: nextData } };
}
